import React, { useEffect, useState, useRef, useCallback } from 'react';
import Accordion from 'b2b-ui-components-lib/lib/components/accordion';
import RadioGroup from 'b2b-ui-components-lib/lib/components/radioGroup';
import Input from 'b2b-ui-components-lib/lib/components/input';
import TextArea from 'b2b-ui-components-lib/lib/components/textarea';
import { isFormValid } from '../../../../common/utils/validator';
import {
  accountDetailSchema,
  billableIdFormKey,
  billingTypeOption,
  BILLING_TYPES,
  BILLING_TYPES_IDs,
  getBCPOptions,
  getDefaultValue,
  keysToIgnoreForExistingAddress,
  mapToFormSchema
} from './formControl';
import { fetchAccountDetailsByAccountNumber, fetchBCPs, fetchCityStateList, fetch_BA_Accounts } from '../../../../common/services/http.service';
import { addresses, ADDRESS_TYPE_ENUM } from '../summary.constant';
import Select from 'b2b-ui-components-lib/lib/components/select';
import Autocomplete from 'b2b-ui-components-lib/lib/components/autocomplete';
import { prepareOptionsForDropDown } from '../../../services/summary.service';
import { handleError, numberSort, showServiceError, removeAsciiCode, filterAccounts } from 'common/utils/app.utils';
import Tooltip from 'common/components/tooltip-component/tooltip-component';
import { TOOLTIP_MESSAGE } from 'home/view/manage-feasibility/constants/multiSite-constants';
import debounce from 'lodash.debounce';

const BillingDetails = ({ order, updateParentForm, setFormStatus, isOrderConfirmation = false, setOrderDetail = () => {}, setCustomerAccounts = () => {} }) => {
  const [accordionMetadata, setAccordionMetadata] = useState({ isValidated: false });
  const [cityList, setCityList] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [form, setForm] = useState(() => getDefaultValue(order));
  const [bcpIdList, setBCPsList] = useState([]);
  const bcpCompleteList = useRef([]);
  const [showLoader, setShowLoader] = useState(false);
  const [poIdsList, setPoIdsList] = useState([]);

  const [accountNumbers, setAccountNumbers] = useState([]);

  const onChangeBillingType = (selectedType) => {
    setForm({ ...form, type: selectedType });

    if (selectedType?.id === 'PO' && form?.accountNumber?.value?.key) {
      fetchBCPByAccount(form.accountNumber?.value?.key);
    }
  };

  const onCityChange = (value) => setForm({ ...form, city: { ...form.city, value, touched: true } });
  const onAddressChange = (value) => setForm({ ...form, address: { ...form.address, value, touched: true, errors: form.address.validator(value) } });

  const onPersonNameChange = (value) => setForm({ ...form, personName: { ...form.personName, value, touched: true, errors: form.personName.validator(value) } });

  const onEmailChange = (value) => setForm({ ...form, email: { ...form.email, value, touched: true, errors: form.email.validator(value) } });

  const onMobileNoChange = (value) => setForm({ ...form, mobileNumber: { ...form.mobileNumber, value, touched: true, errors: form.mobileNumber.validator(value) } });

  const onPinCodeChange = (value) => {
    setForm({
      ...form,
      pinCode: { ...form.pinCode, value, touched: true, errors: form.pinCode.validator(value) },
      city: { ...form.city, value: '', touched: true },
      address: { ...form.address, value: '', touched: true }
    });
  };

  const onDropDownChange = (value, key) => {
    if (value) {
      setForm({
        ...form,
        [key]: { ...form[key], value, touched: true, errors: [] }
      });
      return;
    }
    setForm({
      ...form,
      [key]: {
        ...form[key],
        value,
        touched: true,
        errors: ['This field is mandatory']
      }
    });
  };

  const onBilliableIdChange = (value) => {
    setForm({ ...form, billableId: { ...form.billableId, value, touched: true } });
    const suggestion = getBCPOptions(bcpCompleteList.current, value);
    setBCPsList(suggestion);
  };

  const fetchBCPByAccount = (account = '') => {
    setShowLoader(true);
    fetchBCPs(account).then(
      ({ data }) => {
        if (data && data.success && data.result) {
          const bcps = data.result && data.result.bcpIds;
          const poIds = data?.result?.poIds;

          if (Array.isArray(bcps)) {
            const bcpsList = prepareOptionsForDropDown(bcps, '', numberSort);
            bcpCompleteList.current = bcpsList;
            const suggestion = getBCPOptions(bcpCompleteList.current, '');
            setBCPsList(suggestion);
          }

          if (Array.isArray(poIds)) {
            const poIdsList = prepareOptionsForDropDown(poIds, '', numberSort);
            poIdsList.unshift({ key: 'New', displayName: 'New', value: 'New' });

            setPoIdsList(poIdsList);
          }
        }
        setShowLoader(false);
      },
      (error) => {
        bcpCompleteList.current = [];
        handleError(error);
        setShowLoader(false);
      }
    );
  };

  const onAccountNumberChange = (value) => {
    setForm({ ...form, accountNumber: { ...form.accountNumber, value, touched: true }, billableId: { ...form.billableId, value: '', touched: true } });
    setBCPsList([]);
  };

  const onPoIdChange = (value) => {
    setForm({ ...form, poId: { ...form.poId, value, touched: true } });
  };

  const onExistingAddress = (address) => {
    setForm({ ...form, existingAddress: address });
  };

  const getBAAccounts = (partyId) => {
    fetch_BA_Accounts(partyId).then(
      ({ data }) => {
        if (data && data.success && data.result) {
          const accounts = data.result && data.result.accounts;
          if (Array.isArray(accounts)) {
            setCustomerAccounts(accounts);
            const accountList = prepareOptionsForDropDown(filterAccounts(accounts, partyId), 'parentAccountNumber', numberSort);
            setAccountNumbers(accountList);
            if (accountList.length === 1) {
              setForm({ ...form, accountNumber: { ...form.accountNumber, value: accountList[0], touched: true } });
            }
          }
        }
      },
      (error) => {
        handleError(error);
      }
    );
  };

  const onFieldBlur = (formElement, keyName) => {
    const { value, validator, errors: existingError } = formElement;
    let errors = [];
    if (typeof validator === 'function') {
      errors = validator(value);
    }
    setForm({ ...form, [keyName]: { ...form[keyName], value, errors: errors.concat(existingError), touched: true } });
  };

  const clearBlliableId = () => {
    setForm({ ...form, billableId: { ...form.billableId, value: '', touched: true } });
  };

  const fetchBCPDetails = (bcpId = '') => {
    fetchAccountDetailsByAccountNumber(bcpId).then(
      ({ data }) => {
        if (data) {
          const bcpsDetails = data.result;
          const bcpDetailsForOrder = mapToFormSchema(data.result, order);
          const city = { displayName: bcpsDetails.city, keyName: bcpsDetails.city };
          const address = bcpsDetails?.addressLine1 ? bcpsDetails.addressLine1 + (bcpsDetails.addressLine2 ? ` ${bcpsDetails.addressLine2}` : '') : '';
          const addressForm = {
            pinCode: { ...form.pinCode, value: bcpsDetails.pincode, touched: true },
            city: { ...form.city, value: city, touched: true },
            billableId: { ...form.billableId, value: bcpId, touched: true },
            address: { ...form.address, value: address, touched: true },
            email: { ...form.email, value: bcpsDetails?.emailAddress },
            personName: { ...form.personName, value: bcpsDetails?.firstName },
            mobileNumber: { ...form.mobileNumber, value: bcpsDetails?.phoneNumber }
          };
          setForm((prevForm) => ({ ...prevForm, ...addressForm }));
        }
      },
      (error) => {
        handleError(error);
      }
    );
  };

  const triggerBCPFetch = useCallback(debounce(fetchBCPDetails, 100), []);

  const onAutoCompleteSelect = (suggestion) => {
    // setForm({ ...form, billableId: { ...form.billableId, value: suggestion.value, touched: true } });
    triggerBCPFetch(suggestion?.key);
  };

  const getValue = (suggestion) => {
    return suggestion.value;
  };

  const onBillingTypeChange = (value) => {
    setForm({ ...form, selected: { ...form.selected, value, touched: true }, billableId: { ...form.billableId, value: '', touched: true } });
  };

  useEffect(() => {
    if (form.accountNumber?.value?.key) {
      fetchBCPByAccount(form.accountNumber?.value?.key);
    }
  }, [form.accountNumber?.value?.key]);

  useEffect(() => {
    const {
      lead: {
        customer: {
          authorizedSignatory: {
            address: { state }
          },
          partyId
        }
      }
    } = order;
    const payload = { contactType: 'BCP', partyId, state };
    getBAAccounts(partyId);
  }, []);

  useEffect(() => {
    if (form.pinCode.errors.length === 0 && form.pinCode.value) {
      fetchCityStateList(form.pinCode.value).then(
        ({ data: { result } }) => {
          if (Array.isArray(result)) {
            const cityList = prepareOptionsForDropDown(result, 'city');
            setCityList(cityList);
            if (cityList.length === 1) {
              onCityChange(cityList[0]);
            }
          }
        },
        (err) => {
          let errMsg = err && err.response && err.response.data && err.response.data.errorMsg;
          setCityList([]);
          setForm({
            ...form,
            pinCode: { ...form.pinCode, touched: true, errors: [errMsg ? errMsg : 'Please enter valid Pincode'] },
            city: { ...form.city, value: '', touched: true },
            address: { ...form.address, value: '', touched: true }
          });
          console.error(err);
        }
      );
    }
  }, [form.pinCode.value]);

  useEffect(() => {
    const keyToIgnore = form.selected.value?.id === BILLING_TYPES_IDs.EXISTING ? keysToIgnoreForExistingAddress : [BILLING_TYPES_IDs.EXISTING, billableIdFormKey, 'poId'];
    if (isFormValid(form, keyToIgnore)) {
      let formattedForm = {};
      if (form?.address?.value) {
        formattedForm = { ...form, address: { value: removeAsciiCode(form?.address?.value) } };
      }
      setAccordionMetadata({ isValidated: true });
      updateParentForm({ billingDetails: formattedForm });
      setFormStatus({ billingDetails: true });
    } else {
      setAccordionMetadata({ isValidated: false });
      updateParentForm({ billingDetails: null });
      setFormStatus({ billingDetails: false });
    }
  }, [form]);

  return (
    <React.Fragment>
      <Accordion
        title="Billing Details"
        autoScrollOnExpand={false}
        isExpanded={true}
        isValidated={accordionMetadata.isValidated}
        isEditable={accordionMetadata.isEditable}
        onEditClick={() => setAccordionMetadata(({ isEditable }) => ({ ...accordionMetadata, isEditable: !isEditable }))}
      >
        <div className="accordion_body">
          <div className="form_area">
            <h6>Choose a Billing Account to get the billing related details or enter the required details manually.</h6>
            <div className="row field_row">
              <div className="flg-lg-12 flg-md-12">
                <RadioGroup options={BILLING_TYPES} value={form.selected.value} onChange={onBillingTypeChange} disabled={isOrderConfirmation} />
              </div>

              <div className="flg-lg-6 flg-md-6">
                <Select
                  label="Account Number"
                  value={form.accountNumber.value}
                  onChange={onAccountNumberChange}
                  options={accountNumbers}
                  placeholder="Account Number"
                  required
                  disabled={isOrderConfirmation}
                />
              </div>

              <div className="flg-lg-6 flg-md-6">
                {form.selected.value.id === BILLING_TYPES_IDs.EXISTING && (
                  <div className="field_group">
                    <Autocomplete
                      label="BCP ID"
                      inputValue={form.billableId?.value}
                      onChange={onBilliableIdChange}
                      suggestions={bcpIdList}
                      filterSuggestions={true}
                      onSelect={onAutoCompleteSelect}
                      getValue={getValue}
                      placeholder="BCP ID"
                      autoSelect={false}
                      required
                      disabled={isOrderConfirmation}
                      key="BCP ID"
                    />
                    {form.billableId.value && <div class="cross_icon" onClick={clearBlliableId}></div>}
                    {form.billableId?.value?.length > 0 && bcpIdList.length === 0 && <p className="error"> * Please select BCP Id from list.</p>}
                    {showLoader && <span className="loading_icon"></span>}
                    <Tooltip message={TOOLTIP_MESSAGE.BCP_ID}>
                      <div className="anchor_link">
                        <span className="material-icons-outlined">info</span>
                        <span role="button">Know More</span>
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>
            <div className="devider_group">
              <div className="form_area">
                <h6>Billing Level</h6>
                <RadioGroup options={billingTypeOption} value={form.type} onChange={onChangeBillingType} disabled={isOrderConfirmation} />
                <div className="row field_row">
                  {form?.type?.id === 'PO' && (
                    <div className="flg-lg-6 flg-md-6">
                      <div className="field_tooltip">
                        <Select
                          label="Purchase Order ID"
                          value={form.poId?.value}
                          onChange={onPoIdChange}
                          options={poIdsList}
                          placeholder="Purchase Order ID"
                          required
                          disabled={isOrderConfirmation}
                        />
                      </div>
                    </div>
                  )}
                  <div className="flg-lg-6 flg-md-6">
                    <div className="field_tooltip">
                      <Select
                        label="Billing Payment Type"
                        value={form.paymentType.value}
                        onChange={(value) => {
                          onDropDownChange(value, 'paymentType');
                        }}
                        options={form.paymentType.options}
                        placeholder="Billing Payment Type"
                        required
                        disabled={isOrderConfirmation}
                      />
                    </div>
                  </div>
                  <div className="flg-lg-6 flg-md-6">
                    <div className="field_tooltip">
                      <Select
                        label="Billing Payment Cycle"
                        value={form.paymentCycle.value}
                        onChange={(value) => {
                          onDropDownChange(value, 'paymentCycle');
                        }}
                        options={form.paymentCycle.options}
                        placeholder="Billing Payment Cycle"
                        required
                        disabled={isOrderConfirmation}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form_area">
                <h6>Billing Address</h6>
                {form.selected.id === ADDRESS_TYPE_ENUM.EXISTINGADDRESS ? (
                  <div className="row field_row">
                    <div className="flg-lg-12">
                      <Select label="Address" value={form.existingAddress} onChange={onExistingAddress} options={addressList} disabled={isOrderConfirmation} required />
                    </div>
                  </div>
                ) : (
                  <React.Fragment>
                    <div className="row field_row">
                      <div className="flg-lg-6 flg-md-6">
                        <Input
                          label="Pincode"
                          maxlength={6}
                          value={form.pinCode.value ? form.pinCode.value : ''}
                          isValidChange={(value) => /^(\s*|\d+)$/.test(value)}
                          onBlur={() => onFieldBlur(form.pinCode, 'pinCode')}
                          required
                          errors={form.pinCode.errors}
                          touched={form.pinCode.touched}
                          onChange={onPinCodeChange}
                          disabled={isOrderConfirmation || form.selected.value.id === BILLING_TYPES_IDs.EXISTING}
                        />
                      </div>
                      <div className="flg-lg-6 flg-md-6">
                        <Select
                          label="City"
                          value={form.city.value}
                          onChange={onCityChange}
                          options={cityList}
                          placeholder="City"
                          required
                          disabled={isOrderConfirmation || form.selected.value.id === BILLING_TYPES_IDs.EXISTING}
                        />
                      </div>
                    </div>
                    <div className="row field_row">
                      <div className="flg-lg-12">
                        <TextArea
                          label="Address"
                          maxlength={250}
                          required
                          value={form.address.value}
                          onBlur={() => onFieldBlur(form.address, 'address')}
                          errors={form.address.errors}
                          touched={form.address.touched}
                          onChange={onAddressChange}
                          disabled={isOrderConfirmation || form.selected.value.id === BILLING_TYPES_IDs.EXISTING}
                        />
                      </div>
                      <div style={{ position: 'relative' }}>
                        <small id="helpId" className="help-text-counter">
                          {(form.address.value && form.address.value.length) || 0} /250
                        </small>
                      </div>
                    </div>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>

          <div className="form_area">
            <h6>Billing Contact Details</h6>
            <div className="row field_row">
              <div className="flg-lg-6 flg-md-6">
                <Input
                  label="Contact Person Name"
                  maxlength={150}
                  required
                  value={form.personName.value}
                  onBlur={() => onFieldBlur(form.personName, 'personName')}
                  errors={form.personName.errors}
                  touched={form.personName.touched}
                  onChange={onPersonNameChange}
                  disabled={isOrderConfirmation || form.selected.value.id === BILLING_TYPES_IDs.EXISTING}
                />
              </div>
              <div className="flg-lg-6 flg-md-6">
                <Input
                  label="Contact Person Number"
                  maxlength={10}
                  required
                  isValidChange={(value) => /^(\s*|\d+)$/.test(value)}
                  value={form.mobileNumber.value ? form.mobileNumber.value : ''}
                  onBlur={() => onFieldBlur(form.mobileNumber, 'mobileNumber')}
                  errors={form.mobileNumber.errors}
                  touched={form.mobileNumber.touched}
                  onChange={onMobileNoChange}
                  disabled={isOrderConfirmation || form.selected.value.id === BILLING_TYPES_IDs.EXISTING}
                />
              </div>
            </div>
            <div className="row field_row">
              <div className="flg-lg-6 flg-md-6">
                <Input
                  label="Contact Person Email"
                  required
                  value={form.email.value}
                  onBlur={() => onFieldBlur(form.email, 'email')}
                  errors={form.email.errors}
                  touched={form.email.touched}
                  onChange={onEmailChange}
                  disabled={isOrderConfirmation || form.selected.value.id === BILLING_TYPES_IDs.EXISTING}
                />
              </div>
            </div>
          </div>
        </div>
      </Accordion>
    </React.Fragment>
  );
};

export default BillingDetails;
